import { HttpClient } from '@angular/common/http';
import { Injectable, NgZone, inject } from '@angular/core';
import { MatDialog } from '@angular/material/dialog';
import { NavigationEnd, Router } from '@angular/router';
import { BehaviorSubject, EMPTY, Observable } from 'rxjs';
import { filter, finalize, map, switchMap, take } from 'rxjs/operators';
import { TranslateService } from '@ngx-translate/core';
import { JwtSecurityUser } from '@portal/models/jwtSecurityUser';
import { appendLangParam, getHttpParams, replaceId } from '@portal/core';
import { LocalStorageService } from '@portal/services';
import { AuthService } from '../auth.service';
import { clientContext } from '../client.context';
import { TermsOfUseDialogComponent, TermsOfUseDialogData } from './terms-of-use-dialog.component';

export interface TermsOfUseContext {
  userId?: number;
  termsPagePath?: string;
  buyerBaseUrl?: string;
}

const TERMS_STORAGE_KEY = 'terms_of_use_pending';

@Injectable({
  providedIn: 'root',
})
export class TermsOfUseService {
  private readonly dialog = inject(MatDialog);
  protected readonly auth = inject(AuthService);
  private readonly http = inject(HttpClient);
  private readonly ngZone = inject(NgZone);
  private readonly localStorage = inject(LocalStorageService);
  private readonly router = inject(Router);
  private readonly translateService = inject(TranslateService);

  private readonly pendingSubject = new BehaviorSubject<boolean>(false);
  readonly pending$ = this.pendingSubject.asObservable();

  constructor() {
    this.router.events.pipe(filter(event => event instanceof NavigationEnd)).subscribe(() => this.restoreIfPending());
  }

  get isPending(): boolean {
    return this.pendingSubject.getValue();
  }

  waitForResolution(): Observable<boolean> {
    return this.pending$.pipe(
      filter(pending => !pending),
      take(1),
    );
  }

  restoreIfPending(options?: { skipRenew?: boolean }): void {
    const ctx: TermsOfUseContext | undefined = this.localStorage.get(TERMS_STORAGE_KEY);

    if (ctx && !this.isPending && this.auth.parsedToken) {
      this.handleTermsRequired(ctx, options).subscribe({ next: () => {} });
    }
  }

  handleTermsRequired(
    ctx: TermsOfUseContext,
    options?: { skipRenew?: boolean; skipPersist?: boolean },
  ): Observable<void> {
    if (this.isPending) {
      return this.waitForResolution().pipe(switchMap(() => EMPTY));
    }

    const { userId, termsPagePath, buyerBaseUrl = '' } = ctx;
    const rawTermsPageUrl = buyerBaseUrl
      ? new URL(termsPagePath ?? '', buyerBaseUrl).toString()
      : (termsPagePath ?? '');
    const termsPageUrl = appendLangParam(
      rawTermsPageUrl,
      this.translateService.currentLang || this.translateService.defaultLang,
    );
    const resolvedUserId = userId ?? this.getUserIdFromToken();

    this.pendingSubject.next(true);
    if (!options?.skipPersist) {
      this.localStorage.set(TERMS_STORAGE_KEY, ctx);
    }

    const data: TermsOfUseDialogData = { termsPageUrl };
    const dialogRef = this.dialog.open<TermsOfUseDialogComponent, TermsOfUseDialogData, boolean>(
      TermsOfUseDialogComponent,
      {
        data,
        disableClose: true,
        closeOnNavigation: false,
        backdropClass: 'terms-of-use-backdrop',
        width: '500px',
      },
    );

    return dialogRef.afterClosed().pipe(
      take(1),
      switchMap((accepted: boolean | undefined) => {
        if (accepted === undefined) {
          // Dialog was closed externally (e.g. back button / navigation) — keep localStorage, NavigationEnd will reopen
          return EMPTY;
        }

        if (!accepted) {
          this.localStorage.delete(TERMS_STORAGE_KEY);
          this.ngZone.run(() => this.auth.logOut(true));
          return EMPTY;
        }

        return this.acceptTerms(resolvedUserId).pipe(
          options?.skipRenew ? map(() => undefined as void) : switchMap(() => this.auth.renewAccessTokenSilently()),
          finalize(() => this.localStorage.delete(TERMS_STORAGE_KEY)),
        );
      }),
      finalize(() => this.pendingSubject.next(false)),
    );
  }

  private acceptTerms(userId: number): Observable<unknown> {
    return this.http.patch(replaceId(clientContext.termsUse, userId), null, {
      params: getHttpParams({ skipErrorHandling: true }),
    });
  }

  private getUserIdFromToken(): number {
    const user: JwtSecurityUser = JSON.parse(this.auth.parsedToken.user);

    return user.id;
  }
}
